/**
 * CallLogTab — /phone v2's Call Log surface (iter 507).
 *
 * A true CALL LOG: every call (in and out) as its own row, sorted by
 * most-recent-first. Distinct from the sibling InboxTab which rolls up
 * activity per phone number (great for triage; useless for auditing
 * "who called at 3pm").
 *
 * Data:  GET /api/twilio/recent-calls?limit=200
 *        (merges incoming_calls + twilio_call_logs + ai_call_sessions;
 *         every row carries `contact_id`, `outcome`, `answered_by_name`)
 *
 * Filters (URL-stateful so bookmarks land):
 *   ?dir       = all | in | out
 *   ?outcome   = all | answered | missed | voicemail
 *   ?q         = free text search (contact / number)
 *   ?since     = today | 7d | 30d | all
 *
 * Row actions:
 *   * unresolved number  → inline "Add to Contacts" (via QuickAddContactModal)
 *   * matched contact    → link into /admin/contact-registry for edit/merge
 *   * missed call        → mark-handled toggle
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, Voicemail as VMIcon,
  UserPlus, Bot, Search, CircleCheck, ExternalLink, RefreshCw, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { callsAPI } from "../../services/api";
import { formatPhoneNumber } from "../../lib/utils";
import { NAVY, GOLD, GREEN, RED, AMBER } from "./tokens";
import QuickAddContactModal from "../../components/comms/QuickAddContactModal";
import { useContactSlideover } from "../../components/contacts/ContactDetailSlideover";

function last10(raw) {
  return String(raw || "").replace(/\D/g, "").slice(-10);
}

function fmtWhen(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "—";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const dayMs = 86_400_000;
  const t = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  if (sameDay) return `Today · ${t}`;
  if (Date.now() - d.getTime() < 6 * dayMs) {
    return `${d.toLocaleDateString("en-US", { weekday: "short" })} · ${t}`;
  }
  return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })} · ${t}`;
}

function fmtDur(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  if (s === 0) return "—";
  if (s < 60) return `0:${s.toString().padStart(2, "0")}`;
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;
}

const OUTCOME_META = {
  answered:  { label: "Answered",  color: GREEN, icon: CircleCheck },
  missed:    { label: "Missed",    color: RED,   icon: PhoneMissed },
  voicemail: { label: "Voicemail", color: AMBER, icon: VMIcon },
};

const SINCE_MS = {
  today: () => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t.getTime();
  },
  "7d":  () => Date.now() - 7  * 86_400_000,
  "30d": () => Date.now() - 30 * 86_400_000,
  all:   () => 0,
};

export default function CallLogTab({ activeCompanyId = null }) {
  const { open: openContactSlideover } = useContactSlideover();
  const [params, setParams] = useSearchParams();
  const dir = params.get("dir") || "all";
  const outcome = params.get("outcome") || "all";
  const q = params.get("q") || "";
  const since = params.get("since") || "7d";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [addFor, setAddFor] = useState(null);          // row awaiting Add-Contact
  const [busyId, setBusyId] = useState("");            // row currently mark-handled

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const res = await callsAPI.getRecent(200, false);
      setRows(Array.isArray(res.data) ? res.data : (res.data?.calls || []));
    } catch (e) { setErr(e?.response?.data?.detail || e.message || "Failed to load call log"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Filter client-side. Server returns up to 200 rows already scoped
  // by tenant — the filters here are just narrowing.
  const filtered = useMemo(() => {
    const cutoff = (SINCE_MS[since] || SINCE_MS["7d"])();
    const qNorm = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (activeCompanyId && r.company_id && r.company_id !== activeCompanyId) return false;
      if (dir === "in" && r.direction !== "inbound") return false;
      if (dir === "out" && r.direction !== "outbound") return false;
      if (outcome !== "all" && r.outcome !== outcome) return false;
      const ts = r.created_at ? new Date(r.created_at).getTime() : 0;
      if (ts < cutoff) return false;
      if (qNorm) {
        const hay = [
          r.caller_name, r.from_number, r.to_number, r.company_name,
          r.answered_by_name, r.handled_by_name,
        ].join(" ").toLowerCase();
        if (!hay.includes(qNorm)) return false;
      }
      return true;
    });
  }, [rows, dir, outcome, q, since, activeCompanyId]);

  const setP = (patch) => {
    setParams((p) => {
      const n = new URLSearchParams(p);
      for (const [k, v] of Object.entries(patch)) {
        // Iter 508j — `since` uses 'all' as a REAL value (0 cutoff).
        // Deleting `since=all` fell back to '7d' and hid every call
        // older than a week. Only `dir` and `outcome` treat 'all' as
        // "no filter" (their default), so restrict the delete to those.
        if (v == null || v === "") { n.delete(k); continue; }
        if (v === "all" && (k === "dir" || k === "outcome")) { n.delete(k); continue; }
        n.set(k, v);
      }
      return n;
    }, { replace: true });
  };

  const markHandled = async (row) => {
    setBusyId(row.id);
    try {
      const idToUse = row.id || row.call_sid;
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/twilio/calls/${idToUse}/mark-handled`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ handled: !row.handled }),
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(row.handled ? "Reopened" : "Marked handled");
      await load();
    } catch (e) { toast.error(e.message); }
    setBusyId("");
  };

  return (
    <div className="flex flex-col h-full" data-testid="calllog-tab">
      {/* Header + filter bar */}
      <div
        className="flex items-center gap-2 px-4 py-3 border-b bg-white shrink-0 flex-wrap"
        style={{ borderColor: "#E2E8F0" }}
      >
        <div className="flex items-center gap-2 mr-3">
          <Phone className="w-4 h-4" style={{ color: NAVY }} />
          <div className="font-bold text-[15px]" style={{ color: NAVY }}>Call Log</div>
          <div className="text-[11px] text-slate-500 font-medium">
            {filtered.length} / {rows.length} calls
          </div>
        </div>

        {/* Direction */}
        <FilterPill options={[
          { v: "all", l: "All" }, { v: "in", l: "Inbound" }, { v: "out", l: "Outbound" },
        ]} value={dir} onChange={(v) => setP({ dir: v })} testid="calllog-dir" />

        {/* Outcome */}
        <FilterPill options={[
          { v: "all",       l: "All outcomes" },
          { v: "answered",  l: "Answered" },
          { v: "missed",    l: "Missed" },
          { v: "voicemail", l: "Voicemail" },
        ]} value={outcome} onChange={(v) => setP({ outcome: v })} testid="calllog-outcome" />

        {/* Since */}
        <FilterPill options={[
          { v: "today", l: "Today" }, { v: "7d", l: "7 days" },
          { v: "30d",   l: "30 days" }, { v: "all", l: "All time" },
        ]} value={since} onChange={(v) => setP({ since: v })} testid="calllog-since" />

        <div className="relative ml-auto flex items-center">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="search"
            value={q}
            onChange={(e) => setP({ q: e.target.value })}
            placeholder="Search name or number"
            className="h-8 pl-8 pr-2 rounded-md border text-[12px] bg-white w-52"
            style={{ borderColor: "#E2E8F0" }}
            data-testid="calllog-search"
          />
        </div>

        <button
          onClick={load}
          className="h-8 px-2 rounded-md border text-[12px] font-semibold hover:bg-slate-50"
          style={{ borderColor: "#E2E8F0", color: NAVY }}
          data-testid="calllog-refresh"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {err && (
          <div className="mx-4 mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-800" data-testid="calllog-error">
            {err}
          </div>
        )}
        {loading && rows.length === 0 && (
          <div className="text-center py-10 text-[12px] text-slate-400">Loading…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-12 px-4" data-testid="calllog-empty">
            <div className="text-[14px] font-medium" style={{ color: NAVY }}>
              {rows.length === 0 ? "No calls yet" : "Nothing matches those filters"}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              {rows.length === 0
                ? "Once someone calls in or out, the row will land here."
                : "Try broadening the time range or clearing the search."}
            </div>
          </div>
        )}

        {filtered.length > 0 && (
          <table className="w-full text-[13px]">
            <thead style={{ background: "#F1F5F9" }} className="sticky top-0 z-10">
              <tr className="text-left">
                <Th w="30%">Contact / number</Th>
                <Th w="14%">When</Th>
                <Th w="8%">Duration</Th>
                <Th w="12%">Direction</Th>
                <Th w="12%">Outcome</Th>
                <Th w="14%">Handler</Th>
                <Th w="10%">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <CallRow key={`${r.source}-${r.id}-${r.call_sid}`} r={r}
                          onAdd={setAddFor}
                          onMarkHandled={markHandled}
                          onOpenContact={openContactSlideover}
                          busy={busyId === r.id} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add-contact modal */}
      {addFor && (
        <QuickAddContactModal
          open={true}
          phone={addFor.direction === "inbound" ? addFor.from_number : addFor.to_number}
          defaultName={addFor.caller_name || ""}
          onClose={(saved) => {
            setAddFor(null);
            if (saved) load();
          }}
        />
      )}
    </div>
  );
}

function Th({ children, w }) {
  return (
    <th
      className="px-3 py-2 font-bold text-[11px] uppercase tracking-wider text-slate-500"
      style={{ width: w }}
    >
      {children}
    </th>
  );
}

function FilterPill({ options, value, onChange, testid }) {
  return (
    <div className="inline-flex rounded-md border bg-white overflow-hidden" style={{ borderColor: "#E2E8F0" }} data-testid={testid}>
      {options.map((o) => (
        <button
          key={o.v}
          onClick={() => onChange(o.v)}
          className="px-2.5 py-1 text-[11px] font-semibold border-r last:border-r-0 transition"
          style={{
            borderColor: "#E2E8F0",
            background: value === o.v ? NAVY : "white",
            color: value === o.v ? "white" : NAVY,
          }}
          data-testid={`${testid}-${o.v}`}
        >
          {o.l}
        </button>
      ))}
    </div>
  );
}

function CallRow({ r, onAdd, onMarkHandled, busy, onOpenContact }) {
  const otherNum = r.direction === "inbound" ? r.from_number : r.to_number;
  const ourNum   = r.direction === "inbound" ? r.to_number : r.from_number;
  const name = (r.caller_name && r.caller_name.trim()) || "";
  const meta = OUTCOME_META[r.outcome] || OUTCOME_META.answered;
  const OutcomeIcon = meta.icon;
  const DirIcon = r.outcome === "missed"    ? PhoneMissed
                : r.direction === "inbound" ? PhoneIncoming
                :                              PhoneOutgoing;
  const isAI = r.source === "ai_call_sessions" || r.answered_by === "ai_operator";

  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50" data-testid={`calllog-row-${r.id}`}>
      <td className="px-3 py-2 truncate">
        {name ? (
          <div>
            <div className="font-medium" style={{ color: NAVY }}>
              {/* Iter 508j · click-to-open contract — clicking a caller
                  name (or an unnamed number) opens the canonical
                  ContactDetailSlideover. Falls back to the legacy edit
                  modal only if there's no ContactDetailSlideover hook
                  wired (test / storybook contexts). */}
              <button
                type="button"
                onClick={() => onOpenContact && onOpenContact({ contactId: r.contact_id, phone: otherNum })}
                className="hover:underline text-left"
                data-testid={`calllog-name-${r.id}`}
              >
                {name}
              </button>
            </div>
            <div className="text-[11px] text-slate-500 font-mono">
              {formatPhoneNumber(otherNum)}
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => onOpenContact && onOpenContact({ phone: otherNum })}
            className="text-left hover:underline"
            data-testid={`calllog-unknown-${r.id}`}
          >
            <div className="font-mono text-[13px]" style={{ color: NAVY }}>
              {formatPhoneNumber(otherNum) || "—"}
            </div>
            <div className="text-[11px] text-slate-400">Unknown caller</div>
          </button>
        )}
        {r.company_name && (
          <div className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">
            via {r.company_name}
          </div>
        )}
      </td>
      <td className="px-3 py-2 text-[12px] text-slate-600 whitespace-nowrap">
        {fmtWhen(r.created_at)}
      </td>
      <td className="px-3 py-2 text-[12px] text-slate-600 tabular-nums whitespace-nowrap">
        {fmtDur(r.duration_seconds)}
      </td>
      <td className="px-3 py-2 text-[12px] text-slate-600 whitespace-nowrap">
        <span className="inline-flex items-center gap-1">
          <DirIcon className="w-3.5 h-3.5" style={{ color: r.direction === "inbound" ? NAVY : GOLD }} />
          {r.direction === "inbound" ? "Inbound" : "Outbound"}
          <span className="text-slate-400">·</span>
          <span className="font-mono text-[11px] text-slate-500">{formatPhoneNumber(ourNum)}</span>
        </span>
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        <span
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider"
          style={{ background: meta.color + "22", color: meta.color }}
        >
          <OutcomeIcon className="w-3 h-3" />
          {meta.label}
        </span>
        {r.handled && (
          <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold text-slate-500 border border-slate-200">
            handled
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-[12px] text-slate-600 whitespace-nowrap">
        {isAI ? (
          <span className="inline-flex items-center gap-1">
            <Bot className="w-3.5 h-3.5" style={{ color: NAVY }} /> Ask Avi
          </span>
        ) : (
          r.answered_by_name || r.handled_by_name
          || <span className="text-slate-300">—</span>
        )}
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1">
          {!r.contact_id && (
            <button
              onClick={() => onAdd(r)}
              className="h-7 px-2 rounded text-[11px] font-semibold text-white flex items-center gap-1 hover:opacity-90"
              style={{ background: GREEN }}
              data-testid={`calllog-add-${r.id}`}
              title="Add this caller to Contacts"
            >
              <UserPlus className="w-3 h-3" /> Add
            </button>
          )}
          {r.outcome === "missed" && (
            <button
              onClick={() => onMarkHandled(r)}
              disabled={busy}
              className="h-7 px-2 rounded text-[11px] font-semibold border hover:bg-slate-50 disabled:opacity-50"
              style={{ borderColor: "#E2E8F0", color: NAVY }}
              data-testid={`calllog-handled-${r.id}`}
              title={r.handled ? "Reopen this missed call" : "Mark handled"}
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CircleCheck className="w-3 h-3" />}
              {r.handled ? " Reopen" : " Handled"}
            </button>
          )}
          {r.contact_id && (
            <a
              href="/admin/contact-registry"
              className="h-7 w-7 rounded border flex items-center justify-center hover:bg-slate-50"
              style={{ borderColor: "#E2E8F0", color: NAVY }}
              data-testid={`calllog-registry-${r.id}`}
              title="Open in Contact Registry"
            >
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </td>
    </tr>
  );
}
